import {AfterViewChecked, AfterViewInit, Component, ContentChildren, Input, QueryList} from '@angular/core';
import {MilestoneComponent, MilestoneStatus} from './milestone/milestone.component';


/**
 * Shows a progress bar with milestones with completed and active milestones highlighted.
 *
 * @usageNotes
 *
 * ### Select the currently active milestone (aka next milestone)
 *
 * #### by setting status:
 * ```
 * <app-progress-bar>
 * <app-milestone>First</app-milestone>
 * <app-milestone status="MilestoneStatus.InProgress">Second</app-milestone>
 * </app-progress-bar>
 * ```
 *
 * #### by milestone name:
 * ```
 * <app-progress-bar [nextMilestoneName]="Second">
 * <app-milestone>First</app-milestone>
 * <app-milestone>Second</app-milestone>
 * </app-progress-bar>
 * ```
 *
 * The active milestone set by `nextMilestoneName` is given priority over the ones for which the `status` is specified
 */
@Component({
    selector: 'app-progress-bar',
    templateUrl: './progress-bar.component.html',
    styleUrls: ['./progress-bar.component.css']
})
export class ProgressBarComponent implements AfterViewInit, AfterViewChecked {
    @ContentChildren(MilestoneComponent)
    milestones: QueryList<MilestoneComponent>;
    @Input()
    nextMilestoneName: string;

    ngAfterViewInit(): void {
        this.initializeOrder();
        this.milestones.last.lastMilestone = true;
    }

    ngAfterViewChecked(): void {
        let nextMilestone;

        if (this.nextMilestoneName) {
            const nextMilestoneByName = milestone => (milestone.milestoneName === this.nextMilestoneName);
            nextMilestone = this.milestones.find(nextMilestoneByName);
            nextMilestone.status = MilestoneStatus.InProgress;
        } else {
            nextMilestone = this.milestones.find(milestone => milestone.status === MilestoneStatus.InProgress);
        }

        if (nextMilestone) {
            this.milestones.filter(milestone => milestone.orderNumber < nextMilestone.orderNumber)
                .forEach(milestone => milestone.status = MilestoneStatus.Completed);

            this.milestones.filter(milestone => milestone.orderNumber > nextMilestone.orderNumber)
                .forEach(milestone => milestone.status = MilestoneStatus.Pending);
        }
    }

    private initializeOrder() {
        const milestonesArray = this.milestones.toArray();
        milestonesArray.forEach(milestone => milestone.orderNumber = milestonesArray.indexOf(milestone) + 1);
    }
}
